/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { ref } from 'ember-ref-bucket';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { ComponentLike, ContentValue } from '@glint/template';
interface BvCheckboxSignature {
  Args: {
    disabled?: boolean;
    required?: boolean;
    value?: boolean;
    testName?: string;
    inList?: boolean;
    name?: string;
    checked?: boolean | 'indeterminate';
    id?: string;
    error?: string;
    label?: ContentValue | ComponentLike;
    helpText?: ContentValue;
    labelHidden?: boolean;
    ariaDescribedBy?: string;
    labelClass?: string;
    onChange?: (checked: boolean, event: MouseEvent) => void;
    onFocus?: () => void;
    onBlur?: () => void;
  };
  Element: HTMLElement;
}

export default class BvCheckboxComponent extends Component<BvCheckboxSignature> {

  @tracked checked?: boolean | 'indeterminate' = this.args.checked;

  /**
   * The input element.
   */
  @ref('inputRef') inputRef?: HTMLInputElement;

  /**
   * The id of the checkbox.
   */
  get id() {
    return this.args.id ?? `${guidFor(this)}BvCheckbox`;
  }

  /**
   * Whether the checkbox is in an indeterminate state.
   */
  get isIndeterminate() {
    const { checked = false } = this;
    return checked === 'indeterminate';
  }

  /**
   * Whether the checkbox is checked.
   */
  get isChecked() {
    const { checked } = this;
    return !this.isIndeterminate && Boolean(checked);
  }

  @action
  handleOnClick(e: MouseEvent) {
    const { onChange, disabled } = this.args;

    if ((disabled ?? !this.inputRef) || !onChange) {
      return;
    }
    // Checked and indeterminate state have been changed, but that's not DDAU!
    // Reset the change, send the action and wait for it to be changed manually
    const checked = this.inputRef.checked;
    this.checked = checked;
    const { isChecked, isIndeterminate } = this;
    this.inputRef.checked = isChecked;
    this.inputRef.indeterminate = isIndeterminate;

    onChange(checked, e);

    this.inputRef.focus();
  }

  @action
  handleCheckedChanged(_input: HTMLInputElement, [checked]: [boolean]) {
    if (checked !== this.checked) {
      this.checked = checked;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvCheckbox: typeof BvCheckboxComponent;
    'bv-checkbox': typeof BvCheckboxComponent;
  }
}
